import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  Checkbox,
  Typography,
  Box,
  Button,
  Chip,
  FormControl,
  OutlinedInput,
  ListItemText,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogContent,
  Popper,
  ClickAwayListener,
  MenuList,
  useTheme
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { COLUMNS, FILTER_TYPES, GENDERS, IMAGE_SOURCES } from '../types';
import { 
  fetchSalons, 
  fetchDesigners, 
  fetchHairStyles, 
  fetchHairLengths, 
  fetchBangs, 
  fetchHairColors,
  fetchLoraModels,
  updateImageRecord
} from '../services/api';

const ITEM_HEIGHT = 40;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const DataGrid = forwardRef(({ 
  data, 
  onCellChange, 
  filters, 
  onFilterChange, 
  onRecordSelection, 
  selectedRecords = [],
  onImagePreview,
  getTempFilters,
  filterOptions: externalFilterOptions
}, ref) => {
  const theme = useTheme();
  const [tempFilters, setTempFilters] = useState(filters);  // 임시 필터 상태 추가
  const [filterOptions, setFilterOptions] = useState(externalFilterOptions || {
    image_source_list: Object.values(IMAGE_SOURCES),
    gender_list: Object.values(GENDERS),
    usable_list: [true, false],
    salon_id_list: [],
    designer_id_list: [],
    hair_style_id_list: [],
    hair_length_id_list: [],
    bangs_id_list: [],
    hair_color_id_list: [],
    lora_model_id_list: [],
  });
  
  const [loading, setLoading] = useState({
    salons: false,
    designers: false,
    hairStyles: false,
    hairLengths: false,
    bangs: false,
    hairColors: false,
    loraModels: false,  // LoRA 모델 로딩 상태 추가
  });
  
  // 키보드 네비게이션을 위한 상태
  const [focusedCell, setFocusedCell] = useState({ rowIndex: 0, colIndex: 0 });
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [dropdownSelectedIndex, setDropdownSelectedIndex] = useState(0);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  
  const tableRef = useRef(null);
  const dropdownAnchorRef = useRef(null);

  // 확장된 컬럼 목록 (체크박스 열 추가)
  const extendedColumns = [
    { id: 'checkbox', label: '', width: 50 },
    ...COLUMNS
  ];

  // 외부에서 전달받은 filterOptions가 변경될 때 내부 상태 업데이트
  useEffect(() => {
    if (externalFilterOptions) {
      setFilterOptions(prev => ({
        ...prev,
        ...externalFilterOptions
      }));
    }
  }, [externalFilterOptions]);

  // 미용실, 디자이너, 헤어스타일 등의 API 호출 useEffect들은 제거

  const handleFilterChange = (filterType, value) => {
    const newTempFilters = {
      ...tempFilters,
      [filterType]: value,
    };
    setTempFilters(newTempFilters);
  };

  const handleApplyFilters = () => {
    if (getTempFilters) {
      getTempFilters(tempFilters);
    }
  };

  // 필터 초기화 시 임시 필터도 초기화
  const clearFilters = () => {
    setTempFilters({});
    if (getTempFilters) {
      getTempFilters({});
    }
  };

  // filters prop이 변경될 때 tempFilters도 업데이트
  useEffect(() => {
    setTempFilters(filters);
  }, [filters]);

  const renderFilterValue = (filterType) => {
    const values = tempFilters[filterType] || [];  // filters 대신 tempFilters 사용
    if (values.length === 0) return <em>선택 안함</em>;
    
    if (filterType === 'usable_list') {
      return values.map((value) => (
        <Chip key={value.toString()} label={value ? '사용 가능' : '사용 불가'} size="small" />
      ));
    }
    
    // LoRA 모델 리스트 특별 처리
    if (filterType === 'lora_model_id_list') {
      return values.map((id) => {
        const option = filterOptions[filterType].find(opt => String(opt.id) === String(id));
        return (
          <Chip 
            key={id.toString()} 
            label={option ? option.name : id} 
            size="small" 
          />
        );
      });
    }
    
    // ID 기반 필터의 경우 이름으로 표시
    if (filterType.includes('_id_list') && filterOptions[filterType].length > 0) {
      return values.map((id) => {
        const option = filterOptions[filterType].find(opt => String(opt.id) === String(id));
        return (
          <Chip 
            key={id.toString()} 
            label={option ? option.name : id} 
            size="small" 
          />
        );
      });
    }
    
    return values.map((value) => (
      <Chip key={value.toString()} label={value} size="small" />
    ));
  };

  const renderFilterCell = (column) => {
    // 체크박스 열은 필터 없음
    if (column.id === 'checkbox' || column.id === 'id') {
      return null;
    }
    
    // 컬럼 ID에 해당하는 필터 타입 찾기
    const filterType = Object.keys(FILTER_TYPES).find(key => {
      // lora_model_id_list 특별 처리
      if (column.id === 'lora_model_id_list' && key === 'lora_model_id_list') {
        return true;
      }
      
      const columnName = column.id.replace('_id', '');
      return key.includes(columnName);
    });

    if (!filterType || !filterOptions[filterType]) return null;

    // 로딩 중인 경우 로딩 인디케이터 표시
    if (
      (filterType === 'salon_id_list' && loading.salons) ||
      (filterType === 'designer_id_list' && loading.designers) ||
      (filterType === 'hair_style_id_list' && loading.hairStyles) ||
      (filterType === 'hair_length_id_list' && loading.hairLengths) ||
      (filterType === 'bangs_id_list' && loading.bangs) ||
      (filterType === 'hair_color_id_list' && loading.hairColors) ||
      (filterType === 'lora_model_id_list' && loading.loraModels)  // LoRA 모델 로딩 상태 추가
    ) {
      return <CircularProgress size={24} />;
    }

    return (
      <FormControl fullWidth size="small" sx={{ mt: 1 }}>
        <Select
          multiple
          displayEmpty
          value={tempFilters[filterType] || []}
          onChange={(e) => handleFilterChange(filterType, e.target.value)}
          input={<OutlinedInput />}
          renderValue={() => renderFilterValue(filterType)}
          MenuProps={{
            ...MenuProps,
            PaperProps: {
              ...MenuProps.PaperProps,
              sx: {
                ...MenuProps.PaperProps.style,
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary
              }
            }
          }}
          sx={{
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main
            }
          }}
        >
          <MenuItem value="" disabled>
            <em>선택 안함</em>
          </MenuItem>
          
          {filterType === 'usable_list' ? (
            [true, false].map((option) => (
              <MenuItem key={option.toString()} value={option}>
                <Checkbox checked={(tempFilters[filterType] || []).indexOf(option) > -1} />
                <ListItemText primary={option ? '사용 가능' : '사용 불가'} />
              </MenuItem>
            ))
          ) : filterType === 'lora_model_id_list' ? (
            filterOptions[filterType].map((option) => (
              <MenuItem key={option.id} value={option.id}>
                <Checkbox checked={(tempFilters[filterType] || []).indexOf(option.id) > -1} />
                <ListItemText primary={option.name} />
              </MenuItem>
            ))
          ) : filterType.includes('_id_list') ? (
            filterOptions[filterType].map((option) => (
              <MenuItem key={option.id} value={option.id}>
                <Checkbox checked={(tempFilters[filterType] || []).indexOf(option.id) > -1} />
                <ListItemText primary={option.name} />
              </MenuItem>
            ))
          ) : (
            filterOptions[filterType].map((option) => (
              <MenuItem key={option} value={option}>
                <Checkbox checked={(tempFilters[filterType] || []).indexOf(option) > -1} />
                <ListItemText primary={option} />
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
    );
  };

  // 컬럼에 따른 옵션 목록 가져오기
  const getOptionsForColumn = (columnId) => {
    // 성별 및 이미지 소스 직접 매핑
    if (columnId === 'gender') {
      return Object.values(GENDERS).map(gender => ({ id: gender, name: gender }));
    } else if (columnId === 'image_source') {
      return Object.values(IMAGE_SOURCES).map(source => ({ id: source, name: source }));
    } 
    
    // ID 기반 필드의 옵션 매핑
    const optionsMap = {
      'salon_id': filterOptions.salon_id_list,
      'designer_id': filterOptions.designer_id_list,
      'hair_style_id': filterOptions.hair_style_id_list,
      'hair_length_id': filterOptions.hair_length_id_list,
      'bangs_id': filterOptions.bangs_id_list,
      'hair_color_id': filterOptions.hair_color_id_list,
      'lora_model_id_list': filterOptions.lora_model_id_list
    };

    // 해당 컬럼의 옵션이 있으면 반환, 없으면 빈 배열 반환
    return optionsMap[columnId] || [];
  };
  
  // 셀 값 변경 처리
  const handleCellValueChange = useCallback(async (rowId, columnId, value) => {
    try {
      // 필드 이름 매핑 (API 요청 형식에 맞게)
      const fieldMapping = {
        'salon_id': 'salon_id',
        'designer_id': 'designer_id',
        'hair_style_id': 'hair_style_id',
        'hair_length_id': 'hair_length_id',
        'bangs_id': 'bangs_id',
        'hair_color_id': 'hair_color_id',
        'gender': 'gender',
        'usable': 'usable',
        'caption': 'caption',
        'image_source': 'image_source',
        'lora_model_id_list': 'lora_model_ids'
      };
      
      // lora_model_id_list 특별 처리 (ID 배열로 변환)
      let processedValue = value;
      if (columnId === 'lora_model_id_list') {
        if (Array.isArray(value)) {
          processedValue = value.map(model => typeof model === 'object' ? model.id : model);
        } else if (value && typeof value === 'object') {
          processedValue = [value.id];
        } else if (value) {
          processedValue = [value];
        } else {
          processedValue = [];
        }
      }
      
      // API 요청 데이터 생성
      const updateData = {
        [fieldMapping[columnId] || columnId]: processedValue
      };
      
      // API를 통해 서버에 업데이트
      await updateImageRecord(rowId, updateData);
      
      // 성공 시 로컬 상태 업데이트
      onCellChange(rowId, columnId, value);
    } catch (error) {
      console.error('레코드 업데이트 중 오류가 발생했습니다:', error);
      alert('레코드 업데이트 중 오류가 발생했습니다.');
    }
  }, [onCellChange]);
  
  // 드롭다운 선택 처리
  const handleDropdownSelect = useCallback((option) => {
    if (!openDropdown) return;
    
    const { rowId, columnId } = openDropdown;
    handleCellValueChange(rowId, columnId, option.id);
    setOpenDropdown(null);
  }, [openDropdown, handleCellValueChange]);
  
  // 셀 액션 처리
  const handleCellAction = useCallback(() => {
    if (focusedCell.rowIndex === 0) return;

    const rowIndex = focusedCell.rowIndex - 1;
    const colIndex = focusedCell.colIndex;
    
    if (rowIndex >= 0 && rowIndex < data.length) {
      const row = data[rowIndex];
      const column = extendedColumns[colIndex];
      
      if (!column) return;

      // 이미지 셀인 경우
      if (column.id === 'image_url') {
        setPreviewImageUrl(row.image_url);
        setImagePreviewOpen(true);
        if (onImagePreview) {
          onImagePreview(row.image_url);
        }
        return;
      }

      // 선택 가능한 셀인 경우 드롭다운 표시
      if (column.type === 'select' || column.id.includes('_id')) {
        const options = getOptionsForColumn(column.id);
        if (options.length > 0) {
          setDropdownOptions(options);
          setDropdownSelectedIndex(0);
          setOpenDropdown({ rowId: row.id, columnId: column.id });
        }
      }
    }
  }, [focusedCell, data, extendedColumns, onImagePreview, setPreviewImageUrl, setImagePreviewOpen, setDropdownOptions, setDropdownSelectedIndex, setOpenDropdown]);
  
  // 키보드 이벤트 핸들러
  const handleKeyDown = useCallback((e) => {
    // 드롭다운이 열려있을 때
    if (openDropdown && dropdownOptions.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
        case 'j':
          e.preventDefault();
          setDropdownSelectedIndex(prev => 
            prev < dropdownOptions.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
        case 'k':
          e.preventDefault();
          setDropdownSelectedIndex(prev => 
            prev > 0 ? prev - 1 : 0
          );
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          handleDropdownSelect(dropdownOptions[dropdownSelectedIndex]);
          break;
        case 'Escape':
          e.preventDefault();
          setOpenDropdown(null);
          break;
        default:
          break;
      }
      return;
    }

    // 이미지 미리보기가 열려있을 때
    if (imagePreviewOpen) {
      if (e.key === 'Escape' || e.key === ' ') {
        e.preventDefault();
        setImagePreviewOpen(false);
      }
      return;
    }
    
    // 일반 네비게이션
    let newRowIndex = focusedCell.rowIndex;
    let newColIndex = focusedCell.colIndex;
    let shouldScroll = false;

    switch (e.key) {
      case 'h':
        e.preventDefault();
        newColIndex = Math.max(0, focusedCell.colIndex - 1);
        shouldScroll = true;
        break;
      case 'j':
        e.preventDefault();
        newRowIndex = Math.min(data.length, focusedCell.rowIndex + 1);
        shouldScroll = true;
        // 자동으로 이미지 미리보기 업데이트
        if (newRowIndex <= data.length && newRowIndex > 0) {
          const row = data[newRowIndex - 1];
          if (row && row.image_url) {
            onImagePreview(row.image_url);
          }
        }
        break;
      case 'k':
        e.preventDefault();
        newRowIndex = Math.max(0, focusedCell.rowIndex - 1);
        shouldScroll = true;
        // 자동으로 이미지 미리보기 업데이트
        if (newRowIndex > 0) {
          const row = data[newRowIndex - 1];
          if (row && row.image_url) {
            onImagePreview(row.image_url);
          }
        }
        break;
      case 'l':
        e.preventDefault();
        newColIndex = Math.min(extendedColumns.length - 1, focusedCell.colIndex + 1);
        shouldScroll = true;
        break;
      case 'v':
      case 'm':
        e.preventDefault();
        if (focusedCell.rowIndex > 0) {
          const rowId = data[focusedCell.rowIndex - 1]?.id;
          if (rowId) {
            onRecordSelection(rowId, !selectedRecords.includes(rowId));
          }
        }
        break;
      case ' ':
        e.preventDefault();
        handleCellAction();
        break;
      default:
        break;
    }

    if (shouldScroll) {
      setFocusedCell({ rowIndex: newRowIndex, colIndex: newColIndex });
      
      // 스크롤 처리
      setTimeout(() => {
        const focusedElement = document.querySelector(`[data-row-index="${newRowIndex}"][data-col-index="${newColIndex}"]`);
        if (focusedElement) {
          focusedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 0);
    }
  }, [
    focusedCell, 
    data, 
    openDropdown, 
    dropdownOptions, 
    dropdownSelectedIndex, 
    imagePreviewOpen, 
    selectedRecords, 
    onImagePreview, 
    extendedColumns, 
    onRecordSelection, 
    handleCellAction, 
    handleDropdownSelect, 
    setFocusedCell, 
    setImagePreviewOpen, 
    setOpenDropdown
  ]);
  
  // 키보드 이벤트 리스너 등록
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
  
  // 셀 값의 표시 형식 가져오기
  const getDisplayValueForCell = (columnId, value) => {
    if (value === null || value === undefined) return '';

    // boolean 값 처리
    if (typeof value === 'boolean') {
      return value ? '예' : '아니오';
    }

    // lora_model_id_list 특별 처리
    if (columnId === 'lora_model_id_list' && Array.isArray(value)) {
      if (value.length === 0) return '없음';
      
      // value가 객체 배열인 경우 (UI 표시용 lora_model_id_list)
      if (typeof value[0] === 'object' && value[0].name) {
        return value.map(model => model.name).join(', ');
      }
      
      // value가 ID 배열인 경우 (API 응답의 lora_model_ids)
      const options = getOptionsForColumn('lora_model_id_list');
      return value.map(id => {
        const option = options.find(opt => String(opt.id) === String(id));
        return option ? option.name : `모델 ${id}`;
      }).join(', ');
    }

    // ID 기반 필드 처리
    if (columnId.includes('_id')) {
      const options = getOptionsForColumn(columnId);
      // 문자열로 변환하여 비교
      const option = options.find(opt => String(opt.id) === String(value));
      
      // 디버깅을 위한 로그
      console.log(`컬럼: ${columnId}, 값: ${value}, 찾은 옵션:`, option);
      
      // 옵션이 있으면 이름을, 없으면 원래 값을 반환
      return option ? option.name : value;
    }

    return value;
  };
  
  // 이미지 클릭 처리
  const handleImageClick = (imageUrl) => {
    setPreviewImageUrl(imageUrl);
    setImagePreviewOpen(true);
    if (onImagePreview) {
      onImagePreview(imageUrl);
    }
  };

  // 셀 컨텐츠 렌더링
  const renderCellContent = (row, column, rowIndex, colIndex) => {
    const value = row[column.id];
    const isFocused = focusedCell.rowIndex === rowIndex && focusedCell.colIndex === colIndex;
    
    // 체크박스 열
    if (column.id === 'checkbox') {
      return (
        <Checkbox
          checked={selectedRecords.includes(row.id)}
          onChange={(e) => onRecordSelection(row.id, e.target.checked)}
          inputProps={{ 'aria-label': `선택: ${row.id}` }}
        />
      );
    }

    // ID 열은 단순 표시
    if (column.id === 'id') {
      return (
        <Box
          sx={{
            outline: isFocused ? `2px solid ${theme.palette.primary.main}` : 'none',
            p: 1,
            borderRadius: 1,
          }}
        >
          <Typography variant="body2">{value}</Typography>
        </Box>
      );
    }

    // lora_model_id_list 특별 처리
    if (column.id === 'lora_model_id_list') {
      // 먼저 표시할 데이터 결정
      let displayData = [];
      
      // lora_model_id_list가 있으면 사용 (UI 표시용)
      if (Array.isArray(row.lora_model_id_list) && row.lora_model_id_list.length > 0) {
        displayData = row.lora_model_id_list;
      } 
      // lora_model_ids가 있으면 변환해서 사용 (API 응답)
      else if (Array.isArray(row.lora_model_ids) && row.lora_model_ids.length > 0) {
        const options = getOptionsForColumn('lora_model_id_list');
        displayData = row.lora_model_ids.map(id => {
          const option = options.find(opt => String(opt.id) === String(id));
          return option ? { id, name: option.name } : { id, name: `모델 ${id}` };
        });
      }
      
      return (
        <Box
          sx={{
            outline: isFocused ? `2px solid ${theme.palette.primary.main}` : 'none',
            p: 1,
            borderRadius: 1,
          }}
        >
          {displayData.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {displayData.map((model, idx) => (
                <Chip 
                  key={idx} 
                  label={typeof model === 'object' ? model.name : model} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body2">없음</Typography>
          )}
        </Box>
      );
    }

    // ID 기반 필드나 선택형/boolean 필드는 getDisplayValueForCell 로 표시
    if (column.id.includes('_id') || column.type === 'select' || column.type === 'boolean') {
      return (
        <Box
          sx={{
            outline: isFocused ? `2px solid ${theme.palette.primary.main}` : 'none',
            p: 1,
            borderRadius: 1,
            cursor: 'pointer',
            backgroundColor: isFocused ? theme.palette.action.selected : 'transparent',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            }
          }}
          ref={isFocused ? dropdownAnchorRef : null}
        >
          <Typography variant="body2">
            {getDisplayValueForCell(column.id, value)}
          </Typography>
        </Box>
      );
    }

    // 그 외 일반 케이스
    switch (column.type) {
      case 'image':
        return (
          <Box
            component="img"
            src={value}
            alt="이미지"
            sx={{
              width: '100%',
              height: 90,
              objectFit: 'cover',
              borderRadius: 1,
              cursor: 'pointer',
              outline: isFocused ? `2px solid ${theme.palette.primary.main}` : 'none',
            }}
            onClick={() => handleImageClick(value)}
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/100x80?text=No+Image';
            }}
          />
        );
      default:
        return (
          <Box
            sx={{
              outline: isFocused ? `2px solid ${theme.palette.primary.main}` : 'none',
              p: 1,
              borderRadius: 1,
            }}
          >
            <Typography variant="body2">
              { column.id.includes('_id') ? getDisplayValueForCell(column.id, value) : value }
            </Typography>
          </Box>
        );
    }
  };

  // ref를 통해 외부에서 호출할 수 있는 메서드 정의
  useImperativeHandle(ref, () => ({
    handleApplyFilters: () => {
      console.log('DataGrid handleApplyFilters called');
      if (getTempFilters) {
        getTempFilters(tempFilters);
      }
    },
    clearFilters: () => {
      console.log('DataGrid clearFilters called');
      setTempFilters({});
      if (getTempFilters) {
        getTempFilters({});
      }
    }
  }));

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%', // 부모 컨테이너의 높이에 맞춤
      display: 'flex', 
      flexDirection: 'column'
    }}>
      <Paper sx={{ 
        width: '100%', 
        height: '100%', // 전체 높이 사용
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden' // 내용이 넘치지 않도록
      }}>
        <TableContainer sx={{ 
          height: '100%', // 전체 높이 사용
          overflow: 'auto', // 내용이 넘칠 경우 스크롤 표시
        }}>
          <Table stickyHeader size="small" padding="none">
            <TableHead>
              <TableRow>
                {extendedColumns.map((column, index) => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    style={{ 
                      minWidth: column.width,
                      position: 'sticky',
                      top: 0,
                      zIndex: 1
                    }}
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      padding: '2px 4px' // 패딩 더 축소
                    }}
                  >
                    {column.id === 'checkbox' ? (
                      <Checkbox
                        indeterminate={selectedRecords.length > 0 && selectedRecords.length < data.length}
                        checked={data.length > 0 && selectedRecords.length === data.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const allIds = data.map(row => row.id);
                            allIds.forEach(id => {
                              if (!selectedRecords.includes(id)) {
                                onRecordSelection(id, true);
                              }
                            });
                          } else {
                            const allIds = data.map(row => row.id);
                            allIds.forEach(id => {
                              if (selectedRecords.includes(id)) {
                                onRecordSelection(id, false);
                              }
                            });
                          }
                        }}
                      />
                    ) : (
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {column.label}
                        </Typography>
                        {renderFilterCell(column)}
                      </Box>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, rowIndex) => (
                <TableRow 
                  hover 
                  key={row.id}
                  selected={selectedRecords.includes(row.id)}
                >
                  {extendedColumns.map((column, colIndex) => (
                    <TableCell 
                      key={`${row.id}-${column.id}`} 
                      align="center"
                      onClick={() => setFocusedCell({ rowIndex: rowIndex + 1, colIndex })}
                      data-row-index={rowIndex + 1}
                      data-col-index={colIndex}
                      sx={{ 
                        cursor: 'pointer',
                        p: column.type === 'image' ? 0 : 0.5,
                        backgroundColor: selectedRecords.includes(row.id) 
                          ? theme.palette.action.selected 
                          : 'inherit',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                        ...(column.type === 'image' && {
                          minWidth: column.width,
                          maxWidth: column.width,
                          width: column.width,
                        })
                      }}
                    >
                      {renderCellContent(row, column, rowIndex + 1, colIndex)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* 드롭다운 메뉴 */}
      <Popper
        open={Boolean(openDropdown)}
        anchorEl={dropdownAnchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
      >
        <Paper sx={{ 
          width: 200, 
          maxHeight: 300, 
          overflow: 'auto',
          backgroundColor: theme.palette.background.paper
        }}>
          <ClickAwayListener onClickAway={() => setOpenDropdown(null)}>
            <MenuList>
              {dropdownOptions.map((option, index) => (
                <MenuItem
                  key={option.id}
                  selected={index === dropdownSelectedIndex}
                  onClick={() => handleDropdownSelect(option)}
                  sx={{
                    color: theme.palette.text.primary
                  }}
                >
                  {option.name}
                </MenuItem>
              ))}
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>
      
      {/* 이미지 미리보기 다이얼로그 */}
      <Dialog
        open={imagePreviewOpen}
        onClose={() => setImagePreviewOpen(false)}
        maxWidth="md"
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            backgroundImage: 'none'
          }
        }}
      >
        <DialogContent sx={{ p: 1 }}>
          {previewImageUrl && (
            <img 
              src={previewImageUrl} 
              alt="이미지 미리보기" 
              style={{ maxWidth: '100%', maxHeight: '80vh' }} 
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/300x300?text=이미지+로드+실패';
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
});

export default DataGrid;
